import { pluck, pipe, curriedPluck } from '@/utils/helper'
import FetchEventUseCase from '@/usecases/event/FetchEventUseCase'
import FetchEventAlbumsUseCase from '@/usecases/event/FetchEventAlbumsUseCase'
import SearchEventUchinokoPhotosUseCase from '@/usecases/event/SearchEventUchinokoPhotosUseCase'
import FetchGroupsUseCase from '@/usecases/group/FetchGroupsUseCase'
import FetchSchoolsUseCase from '@/usecases/school/FetchSchoolsUseCase'
import FetchGroupSettingUseCase from '@/usecases/group/FetchGroupSettingUseCase'
import FetchChildrenUseCase from '@/usecases/child/FetchChildrenUseCase'
import FetchCartQueryStatusUseCase from '@/usecases/event/FetchCartQueryStatusUseCase'
import SearchEventCartQueryPhotosUseCase from '@/usecases/event/SearchEventCartQueryPhotosUseCase'
import GroupRepository from '@/repositories/GroupRepository'
import EventRepository from '@/repositories/EventRepository'

import FetchEventsBySchoolsUseCase from '@/usecases/event/FetchEventsBySchoolsUseCase'

import ChildEntity from '@/entities/Child'
import { ICartQueryStatusProps } from '@/entities/C4'
import { GroupNotFoundError } from '@/common/errors/group'

export interface ILoadEventHomeUseCaseParams {
  fetchEventUseCase: FetchEventUseCase
  fetchEventAlbumsUseCase: FetchEventAlbumsUseCase
  searchEventUchinokoPhotosUseCase: SearchEventUchinokoPhotosUseCase
  fetchGroupsUseCase: FetchGroupsUseCase
  fetchSchoolsUseCase: FetchSchoolsUseCase
  fetchGroupSettingUseCase: FetchGroupSettingUseCase
  fetchChildrenUseCase: FetchChildrenUseCase
  fetchEventsBySchoolsUseCase: FetchEventsBySchoolsUseCase
  fetchCartQueryStatusUseCase: FetchCartQueryStatusUseCase
  searchEventCartQueryPhotosUseCase: SearchEventCartQueryPhotosUseCase
  groupRepository: GroupRepository
  eventRepository: EventRepository
}
export default class LoadEventHomeUseCase implements BaseUseCase {
  private fetchEventUseCase: FetchEventUseCase
  private fetchEventAlbumsUseCase: FetchEventAlbumsUseCase
  private searchEventUchinokoPhotosUseCase: SearchEventUchinokoPhotosUseCase
  private fetchGroupsUseCase: FetchGroupsUseCase
  private fetchSchoolsUseCase: FetchSchoolsUseCase
  private fetchGroupSettingUseCase: FetchGroupSettingUseCase
  private fetchChildrenUseCase: FetchChildrenUseCase
  private fetchEventsBySchoolsUseCase: FetchEventsBySchoolsUseCase
  private fetchCartQueryStatusUseCase: FetchCartQueryStatusUseCase
  private searchEventCartQueryPhotosUseCase: SearchEventCartQueryPhotosUseCase
  private groupRepository: GroupRepository
  private eventRepository: EventRepository

  constructor(params: ILoadEventHomeUseCaseParams) {
    this.fetchEventUseCase = params.fetchEventUseCase
    this.fetchEventAlbumsUseCase = params.fetchEventAlbumsUseCase
    this.searchEventUchinokoPhotosUseCase = params.searchEventUchinokoPhotosUseCase
    this.fetchGroupsUseCase = params.fetchGroupsUseCase
    this.fetchSchoolsUseCase = params.fetchSchoolsUseCase
    this.fetchGroupSettingUseCase = params.fetchGroupSettingUseCase
    this.fetchChildrenUseCase = params.fetchChildrenUseCase
    this.fetchEventsBySchoolsUseCase = params.fetchEventsBySchoolsUseCase
    this.fetchCartQueryStatusUseCase = params.fetchCartQueryStatusUseCase
    this.searchEventCartQueryPhotosUseCase = params.searchEventCartQueryPhotosUseCase
    this.groupRepository = params.groupRepository
    this.eventRepository = params.eventRepository
  }

  async execute(eventId: number) {
    try {
      const event = await this.fetchEventUseCase.execute(eventId)

      // FIXME: Use entity method
      // パスワードロックがかかったイベント, ステータスがcloseのイベントに付随するリソースは取得しない
      if ((!event.passwordUnlockFlag && event.passwordType === 'common') || event.publishStatus === 'close') return

      // Groupsとそれに紐づくSchoolsを取得してイベントを閲覧可能なGroupを特定
      // その後上記のGroupSettingsを取得してかお検索設定を確認
      const groups = await this.fetchGroupsUseCase.execute()
      await Promise.all(groups.map(async group => await this.fetchSchoolsUseCase.execute(group.props.id)))
      const targetGroups = this.groupRepository.getGroupsByEvent({ eventId })
      await Promise.all(targetGroups.map(async group => await this.fetchGroupSettingUseCase.execute(group.props.id)))
      const settings = this.groupRepository.getGroupSettings(pluck(pluck(targetGroups, 'props'), 'id'))
      const isUchinokoSearchEnabled = settings.some(setting => setting.isUchinokoSearchEnabled)

      await Promise.all(targetGroups.map(async group => await this.fetchChildrenUseCase.execute(group.props.id)))
      const children = this.groupRepository.getChildrenByEvent(eventId)
      const childrenList = Object.values(children)
        .reduce((acc, ac) => [...acc, ...ac], [] as ChildEntity[])
        .filter(child => child.props.profileImage)
      let hasAdminPrivilege = false
      const adminGroup = this.groupRepository.getAdminGroup()
      if (adminGroup) {
        const adminSchools = this.groupRepository.getSchools(adminGroup.props.id)
        hasAdminPrivilege = adminSchools.some(school => school.props.eventIds.includes(eventId))
      }

      const childIds = pipe(() => childrenList, curriedPluck('props'), curriedPluck('id'))
      const profileSearchEnabledChildren = settings[0].children
        .map(children => {
          if (children.is_profile_image_search_enabled) {
            return children.child_id
          }
        })
        .filter((n): n is number => {
          return typeof n === 'number'
        })

      // isProfileSearchEnabled → 見ている対象のeventに紐づくお子さまのサムネイル登録有かつ常に検索設定がひとつ以上あるか: boolean
      const isProfileSearchEnabled =
        [...profileSearchEnabledChildren, ...childIds].filter(NumbersToCompare => profileSearchEnabledChildren.includes(NumbersToCompare) && childIds.includes(NumbersToCompare)).length > 0
      let cartQueryStatus: ICartQueryStatusProps | undefined

      // かお検索設定がONになっている場合のみ「かお」を取得
      if (isUchinokoSearchEnabled) {
        cartQueryStatus = await this.fetchCartQueryStatusUseCase.execute({ eventId })
        if (cartQueryStatus && cartQueryStatus.hasCartQuery && !cartQueryStatus.hasOrderHistory && hasAdminPrivilege && !isProfileSearchEnabled) {
          // hasCartQuery有り、購入履歴がない、かつ常に検索設定がない場合はsearchEventCartQueryPhotosUseCaseからデータを取得
          await Promise.all([this.searchEventCartQueryPhotosUseCase.execute({ eventId }), this.fetchEventAlbumsUseCase.execute(eventId)])
        } else if (cartQueryStatus && cartQueryStatus.hasOrderHistory) {
          await Promise.all([this.searchEventUchinokoPhotosUseCase.execute({ eventId }), this.fetchEventAlbumsUseCase.execute(eventId)])
        } else if (cartQueryStatus && !cartQueryStatus.hasOrderHistory && isProfileSearchEnabled) {
          await Promise.all([this.searchEventUchinokoPhotosUseCase.execute({ eventId, ignoreOrderHistory: true }), this.fetchEventAlbumsUseCase.execute(eventId)])
        } else {
          this.eventRepository.resetUchinokoData(eventId)
          await this.fetchEventAlbumsUseCase.execute(eventId)
        }
      } else {
        await this.fetchEventAlbumsUseCase.execute(eventId)
      }

      // groupIdの設定
      let defaultGroupId: number
      if (adminGroup) {
        defaultGroupId = adminGroup.props.id
      } else {
        const groups = this.groupRepository.getGroups()
        defaultGroupId = groups[0] && groups[0].props.id
      }
      if (!defaultGroupId) throw new GroupNotFoundError('Cannot find any user families!')

      // school情報の取得
      const schools = await this.fetchSchoolsUseCase.execute(defaultGroupId)

      if (schools.length) {
        const schoolIds = pluck(schools, 'id')

        /* TODO: < 2020/03/30 >
          パスワード解除後に以下のfetchを行うと、
          パスワード解除前のキャッシュされたレスポンスを返してくる（ファミリートップで同じパラメータでgetしている）ため
          リロードやページ遷移時に再度パスワード入力を求められる。（UIでのみ）
          最大5分程度で解消される。
          パスワードありのイベントのみで、あまり取らない行動の際に起こる挙動のため別途対応する形に。
          上記対応を行う場合、UnlockPassword.vueで実行するUsecaseを LoadEventUseCase から LoadEventHomeUseCase
          にする必要がありそう。（要確認）
        */
        await this.fetchEventsBySchoolsUseCase.execute({ schoolIds })
      }
    } catch (error) {
      throw error
    }
  }
}
